import emojiFlags from 'emoji-flags'
import React from 'react'
import styled from 'styled-components'

import {
    invoiceGeneratorActioned,
    BigformActionedAction,
    InvoiceGeneratorActionedField,
} from '@/analytics/segment'
import {DateInput} from '@/components/date-input'
import {FileUpload} from '@/components/file-upload'
import type {Props as InputProps} from '@/components/input'
import Input from '@/components/input'
import Select from '@/components/select'
import type {ClearError, Errors, SetError} from '@/hooks/use-form-errors'
import {useGlobalStrings} from '@/providers/global-strings'
import {useLocale} from '@/providers/locale'
import {colors, tokens} from '@/theme'
import {convertBlobToBase64} from '@/utils/convert-blob-to-base64'
import {getCurrencyName} from '@/utils/get-currency-name'
import mq from '@/utils/media-query'

import {SectionWrapper} from './shared-styles'
import {Actions} from './state'
import type {Dispatch, State, StateKey} from './state'

interface Props {
    title: string
    dispatch: Dispatch
    state: State
    errors: Errors<State>
    setError: SetError<State>
    clearError: ClearError<State>
}

const currenciesMap = {
    DKK: emojiFlags.DK.emoji,
    GBP: emojiFlags.GB.emoji,
    EUR: emojiFlags.EU.emoji,
    USD: emojiFlags.US.emoji,
    SEK: emojiFlags.SE.emoji,
    NOK: emojiFlags.NO.emoji,
}

export const InvoiceDetails = ({title, dispatch, state, errors, setError, clearError}: Props) => {
    const globalStrings = useGlobalStrings()
    const currentLocale = useLocale()

    const handleInputChange = (key: StateKey, value: string | Date) => {
        dispatch({
            type: Actions.SET_VALUES,
            key,
            value,
        })

        invoiceGeneratorActioned({
            action: BigformActionedAction.Interacted,
            field: InvoiceGeneratorActionedField.InvoiceDetails,
        })

        if (!value) {
            setError(key, globalStrings.this_field_is_required)
            return
        }

        clearError(key)
    }

    return (
        <StyledWrapper title={title}>
            <StyledInvoiceInput
                label={globalStrings.invoice_number}
                value={state.invoiceNumber}
                required
                onChange={(value) => {
                    handleInputChange('invoiceNumber', value)
                }}
                error={errors[`invoiceNumber`]}
            />
            <DatesWrapper>
                <DateInput
                    label={globalStrings.issued_on}
                    required
                    value={state.invoiceIssueDate}
                    onChange={(value) => {
                        handleInputChange('invoiceIssueDate', value)
                    }}
                    error={errors[`invoiceIssueDate`]}
                />
                <DateInput
                    label={globalStrings.due_date}
                    required
                    value={state.invoiceDueDate}
                    onChange={(value) => {
                        handleInputChange('invoiceDueDate', value)
                    }}
                    error={errors[`invoiceDueDate`]}
                />
            </DatesWrapper>
            <FileUpload
                id="company-logo-upload"
                label={globalStrings.company_logo}
                allowedFileTypes={['jpg', 'jpeg', 'png', 'svg']}
                fileTypesText={`(${globalStrings.file_type}: jpg, jpeg, png), max 5MB`}
                fileSize={6}
                onReset={() => handleInputChange('yourCompanyLogo', '')}
                onChange={(file) =>
                    convertBlobToBase64(file, (base64string) => {
                        dispatch({
                            type: Actions.SET_VALUES,
                            key: 'yourCompanyLogo',
                            value: base64string,
                        })
                    })
                }
                variant="field"
                error={errors[`yourCompanyLogo`]}
            />
            <StyledSelect
                label={globalStrings.currency}
                options={Object.keys(currenciesMap).map((currency) => ({
                    label: `${
                        currenciesMap[currency as keyof typeof currenciesMap]
                    } ${getCurrencyName(
                        currency as keyof typeof currenciesMap,
                        currentLocale.language,
                    )}`,
                    value: currency,
                }))}
                required
                value={state.currency}
                placeholder=""
                onChange={(value) => {
                    handleInputChange('currency', value)
                }}
            />
        </StyledWrapper>
    )
}

type WrappedInputProps = InputProps & {
    className?: string
}

const WrappedInput = ({className, children, ...props}: WrappedInputProps) => (
    <div className={className}>
        <StyledInput {...props} />
        {children}
    </div>
)

const DatesWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        flex-direction: row;
        grid-column: 1 / span 2;
        grid-row: 2;
    }

    ${mq.min('md')} {
        grid-column: 2;
        grid-row: 1;
    }
`

const StyledInput = styled(Input)`
    background-color: ${colors.background.default};
`

const StyledSelect = styled(Select)`
    & {
        display: flex;
        flex-direction: column;
    }

    button {
        background-color: ${colors.background.default};
        height: 50px;
    }
`

const StyledWrapper = styled(SectionWrapper)`
    display: grid;
    gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        grid-template-columns: 1fr 1fr;
    }

    ${mq.min('md')} {
        column-gap: ${tokens.spacing40};
    }
`

const StyledInvoiceInput = styled(WrappedInput)`
    ${mq.min('sm')} {
        grid-column: 1 / span 2;
    }

    ${mq.min('md')} {
        grid-column: 1;
        grid-row: 1;
    }
`
